/**
 * channel-multiselect.tsx — compact multi-select for notification channels.
 * Sourced from useChannels(); options are the configured channels by name.
 * Selected IDs are passed up via onChange.
 */
import { MultiSelect, type MultiSelectOption, Skeleton } from '@vnetwork/ui/primitives';
import { useChannels } from '@/api/healthcheck';

interface Props {
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}

export function ChannelMultiselect({ value, onChange, disabled }: Props) {
  const { data: channels = [], isLoading } = useChannels();

  if (isLoading) return <Skeleton style={{ height: 38 }} />;

  if (channels.length === 0) {
    return (
      <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
        No channels configured yet. Create one under <strong>Notifications</strong> first.
      </p>
    );
  }

  const options: MultiSelectOption[] = channels.map((ch) => ({ value: ch.id, label: ch.name }));

  return (
    <MultiSelect
      placeholder="Select channels…"
      options={options}
      value={value}
      disabled={disabled}
      onChange={onChange}
    />
  );
}
